import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowRight, ArrowLeft, Calendar, MapPin, Euro, Search } from 'lucide-react';
import { getCategoryList } from '../data/categories';

const EVENT_TYPES = [
  'Hochzeit', 'Geburtstag', 'Firmenfeier', 'Messe', 'Gala', 
  'Konzert', 'Vereinsfeier', 'Kindergeburtstag', 'Jubiläum', 'Sonstiges'
];

const BUDGET_RANGES = [
  'bis 1.000 €',
  '1.000 - 3.000 €',
  '3.000 - 10.000 €',
  'über 10.000 €'
];

const EventConfigurator = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState({
    eventType: '',
    date: '',
    dateFlexible: false,
    city: '',
    zip: '',
    categories: [],
    budget: ''
  });

  const categories = getCategoryList();

  const handleCategoryToggle = (cat) => {
    setConfig(prev => ({
      ...prev,
      categories: prev.categories.includes(cat)
        ? prev.categories.filter(c => c !== cat)
        : [...prev.categories, cat]
    }));
  };

  const handleNext = () => {
    if (step === 1 && !config.eventType) {
      alert('Bitte wähle eine Eventart');
      return;
    }
    if (step === 2 && (!config.city || !config.zip)) {
      alert('Bitte gib Stadt und PLZ ein');
      return;
    }
    if (step === 3 && config.categories.length === 0) {
      alert('Bitte wähle mindestens eine Kategorie');
      return;
    }
    
    if (step < 4) {
      setStep(step + 1);
    } else {
      // Weiterleitung zur Suche mit Filtern
      const searchParams = new URLSearchParams();
      if (config.categories.length > 0) {
        searchParams.set('category', config.categories[0]);
        if (config.categories.length > 1) {
          searchParams.set('categories', config.categories.join(','));
        }
      }
      if (config.city) {
        searchParams.set('city', config.city);
      }
      if (config.zip) {
        searchParams.set('zip', config.zip);
      }
      navigate(`/search?${searchParams.toString()}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br bg-[#F4F2F2]">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8 text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Finde die perfekten Dienstleister für dein Event
            </h1>
            <p className="text-gray-600">Beantworte ein paar Fragen und wir zeigen dir passende Anbieter</p>
          </div>

          {/* Progress */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  className={`flex-1 h-2 rounded-full mx-1 transition-colors ${
                    s <= step ? 'bg-[#2B2142]' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            <p className="text-center text-gray-600">Schritt {step} von 4</p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-4 sm:p-8">
            {/* Step 1: Eventart */}
            {step === 1 && (
              <div data-testid="config-step-1">
                <h2 className="text-2xl font-bold mb-6 text-gray-900">Welche Art von Event planst du?</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                  {EVENT_TYPES.map((type) => (
                    <button
                      key={type}
                      onClick={() => setConfig({ ...config, eventType: type })}
                      className={`p-3 md:p-4 rounded-xl border-2 text-sm md:text-base font-semibold transition-all ${
                        config.eventType === type
                          ? 'border-[#2B2142] bg-[#FFF0D1]/50 text-[#2B2142] scale-105'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`event-type-${type}`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Datum & Ort */}
            {step === 2 && (
              <div data-testid="config-step-2">
                <h2 className="text-2xl font-bold mb-6 text-gray-900">Wann und wo findet das Event statt?</h2>
                
                <div className="space-y-6">
                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <Calendar className="w-5 h-5" />
                      Datum (optional)
                    </label>
                    <input
                      type="date"
                      value={config.date}
                      onChange={(e) => setConfig({ ...config, date: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                      data-testid="event-date"
                    />
                    <label className="flex items-center gap-2 mt-2">
                      <input
                        type="checkbox"
                        checked={config.dateFlexible}
                        onChange={(e) => setConfig({ ...config, dateFlexible: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-gray-600">Datum noch offen / flexibel</span>
                    </label>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <MapPin className="w-5 h-5" />
                      Stadt *
                    </label>
                    <input
                      type="text"
                      value={config.city}
                      onChange={(e) => setConfig({ ...config, city: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                      placeholder="z.B. Stuttgart"
                      data-testid="event-city"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 block">
                      PLZ *
                    </label>
                    <input
                      type="text"
                      value={config.zip}
                      onChange={(e) => setConfig({ ...config, zip: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#2B2142] focus:outline-none"
                      placeholder="z.B. 70173"
                      data-testid="event-zip"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Kategorien */}
            {step === 3 && (
              <div data-testid="config-step-3">
                <h2 className="text-2xl font-bold mb-6 text-gray-900">Welche Dienstleister benötigst du?</h2>
                <p className="text-gray-600 mb-6">Wähle alle Kategorien, die du für dein Event brauchst</p>
                
                <div className="grid grid-cols-2 gap-4">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => handleCategoryToggle(cat)}
                      className={`p-4 rounded-xl border-2 text-sm font-semibold text-left transition-all ${
                        config.categories.includes(cat)
                          ? 'border-[#2B2142] bg-[#FFF0D1]/50 text-[#2B2142]'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`config-category-${cat}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 4: Budget */}
            {step === 4 && (
              <div data-testid="config-step-4">
                <h2 className="text-2xl font-bold mb-6 text-gray-900">Was ist dein Budget-Rahmen?</h2>
                <p className="text-gray-600 mb-6">Dies hilft uns, passende Dienstleister vorzuschlagen</p>
                
                <div className="grid grid-cols-2 gap-4">
                  {BUDGET_RANGES.map((budget) => (
                    <button
                      key={budget}
                      onClick={() => setConfig({ ...config, budget })}
                      className={`p-6 rounded-xl border-2 font-semibold transition-all ${
                        config.budget === budget
                          ? 'border-[#2B2142] bg-[#FFF0D1]/50 text-[#2B2142] scale-105'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`budget-${budget}`}
                    >
                      <Euro className="w-6 h-6 mx-auto mb-2" />
                      {budget}
                    </button>
                  ))}
                </div>

                <div className="mt-8 p-6 bg-gradient-to-br from-[#FFF0D1]/30 to-[#F4F2F2] rounded-2xl">
                  <h3 className="font-bold text-gray-900 mb-3">Deine Event-Konfiguration:</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>✓ Event: <strong>{config.eventType}</strong></li>
                    <li>✓ Ort: <strong>{config.city}, {config.zip}</strong></li>
                    <li>✓ Kategorien: <strong>{config.categories.length} ausgewählt</strong></li>
                    {config.budget && <li>✓ Budget: <strong>{config.budget}</strong></li>}
                  </ul>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8">
              {step > 1 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="flex items-center gap-2 px-6 py-3 text-gray-600 hover:bg-gray-100 rounded-xl font-semibold"
                  data-testid="config-back-btn"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Zurück
                </button>
              )}
              <button
                onClick={handleNext}
                className="ml-auto flex items-center gap-2 px-6 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white font-semibold rounded-xl"
                data-testid="config-next-btn"
              >
                {step === 4 ? (
                  <>
                    Passende Dienstleister finden
                    <Search className="w-5 h-5" />
                  </>
                ) : (
                  <>
                    Weiter
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default EventConfigurator;
